<template>
    <div
        class="bg-white"
        :style="{
            'background-image': 'url(/img/hero-background.svg)',
            'background-position': 'top -20px center',
            'background-size': '1747px 912px',
            'background-repeat': 'no-repeat'
        }"
    >
        <div class="w-full bg-blue h-1-5" />

        <div class="container max-w-3xl mx-auto">
            <div class="max-w-lg py-8 mx-auto md:text-center">
                <router-link
                    :to="{ name: 'www.home.index' }"
                    class="inline-block h-6 mb-6 md:mb-16"
                >
                    <base-svg
                        name="logo-rentora"
                        class="h-full fill-primary-blue"
                    />
                </router-link>

                <h1 class="mb-3 text-4xl font-semibold leading-tight tracking-tight font-noto-sans md:text-5xl xl:text-6xl text-grey-darkest">
                    Market your properties
                    <span class="text-blue">for free</span>
                </h1>

                <h2 class="mb-8 text-lg md:mb-12 md:text-xl xl:text-2xl">
                    Publicly list your properties, and/or invite tenants to apply with a unique link. Every Rentora listing has a built-in rental application.
                </h2>

                <div class="flex flex-col justify-center max-w-md mx-auto mb-4 md:flex-row lg:justify-start">
                    <laroute-link
                        :to="{
                            name: 'auth.users.create',
                            params: {
                                intended: $laroute.route('dashboard.landlord.listings.create', {
                                    welcome: '✓'
                                })
                            }
                        }"
                        class="flex-auto mb-3 shadow-md md:mb-0 md:mr-3 btn btn-blue btn-xl"
                    >
                        Market your properties now
                    </laroute-link>

                    <callback-requests-create-button
                        class="flex-auto shadow-md btn btn-white btn-xl"
                    >
                        Request a callback
                    </callback-requests-create-button>
                </div>
            </div>

            <grid-layout class="hidden mb-12 lg:mb-24">
                <grid-layout-item class="mb-12 lg:mb-0 lg:w-1/3">
                    <div class="inline-block p-3 mb-1 rounded-full bg-blue-lightest">
                        <base-svg
                            name="icon-tune"
                            class="w-8 h-8 fill-secondary-blue fill-primary-blue-lighter"
                        />
                    </div>

                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                        All-in-one solution
                    </h6>

                    <p>
                        List your property, review applications, manage tenancies, perform inventories, share documents, get paid and more - anywhere, on any device, 24/7.
                    </p>
                </grid-layout-item>

                <grid-layout-item class="mb-12 lg:mb-0 lg:w-1/3">
                    <div class="inline-block p-3 mb-1 rounded-full bg-blue-lightest">
                        <base-svg
                            name="icon-money"
                            class="w-8 h-8 fill-primary-blue fill-secondary-blue-lighter"
                        />
                    </div>

                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                        Direct payments
                    </h6>

                    <p>
                        You get paid directly (and quickly). Payments from tenants are processed and deposited directly into your nominated bank account.
                    </p>
                </grid-layout-item>

                <grid-layout-item class="mb-12 lg:mb-0 lg:w-1/3">
                    <div class="inline-block p-3 mb-1 rounded-full bg-blue-lightest">
                        <base-svg
                            name="icon-user-group"
                            class="w-8 h-8 fill-secondary-blue fill-primary-blue-lighter"
                        />
                    </div>

                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                        Landlord and agent friendly
                    </h6>

                    <p>
                        Built to scale, Rentora is usable by everyone. From single property private landlords, to nationwide estate agents.
                    </p>
                </grid-layout-item>
            </grid-layout>
        </div>

        <svg
            class="w-full h-5 text-blue-bg xl:h-10"
            preserveAspectRatio="none"
            viewBox="0 0 375 40"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path d="M375 40V0s-87.046 32-187.5 32S0 0 0 0v40h375z" />
        </svg>

        <div class="bg-blue-bg">
            <div
                class="py-12 bg-center bg-no-repeat md:py-32"
                :style="{
                    'background-image': 'url(/img/misc-background.svg)',
                    'background-position': 'top 150px right -50px',
                    'background-size': '462px 600px'
                }"
            >
                <div class="container max-w-3xl mx-auto">
                    <grid-layout class="flex-col-reverse items-center lg:flex-row">
                        <grid-layout-item class="lg:w-1/2">
                            <h3 class="mb-4 text-2xl font-semibold leading-tight tracking-tight xs:text-3xl font-noto-sans text-grey-darkest">
                                Property listings
                            </h3>

                            <p class="mb-12 text-lg lg:text-xl">
                                Quickly create professional property listings and start marketing your property immediately.
                            </p>

                            <div class="flex flex-col items-start mb-12 md:flex-row">
                                <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                    <base-svg
                                        name="icon-home"
                                        class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                                    />
                                </div>

                                <div class="flex-grow md:pl-6">
                                    <div class="mb-1 text-sm uppercase text-grey-dark">
                                        Listings
                                    </div>

                                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Beautiful listings, in minutes
                                    </h6>

                                    <p class="mb-3">
                                        Show off your properties with beautiful, mobile friendly listings - showcasing a gallery
                                        of photos, a video tour, prices, features, description and more.
                                    </p>

                                    <p>
                                        It doesn’t take long either. In <strong>less than 5 minutes</strong> you could list your property.
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col items-start mb-12 md:flex-row">
                                <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                    <base-svg
                                        name="icon-facebook-circle"
                                        class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                                    />
                                </div>

                                <div class="flex-grow md:pl-6">
                                    <div class="mb-1 text-sm uppercase text-grey-dark">
                                        Social Media
                                    </div>

                                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Built for social media
                                    </h6>

                                    <p>
                                        Private landlords are already moving towards marketing their properties in Facebook groups
                                        over traditional avenues. Our social media friendly property listings compliment this method of marketing.
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col items-start md:flex-row">
                                <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                    <base-svg
                                        name="icon-search"
                                        class="w-8 h-8 fill-primary-blue fill-secondary-blue-lighter"
                                    />
                                </div>

                                <div class="flex-grow md:pl-6">
                                    <div class="mb-1 text-sm uppercase text-grey-dark">
                                        Search
                                    </div>

                                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Super fast property search
                                    </h6>

                                    <p>
                                        Your property gets listed on our website <strong>for free</strong>. Our property search is super
                                        fast, built to scale and gets visted by more and more tenants each day.
                                    </p>
                                </div>
                            </div>
                        </grid-layout-item>
                        <grid-layout-item class="mb-6 lg:mb-0 lg:w-1/2">
                            <img
                                src="/img/property-listing.svg"
                                class="w-full mx-auto"
                                :style="{
                                    'max-width': '410px'
                                }"
                            >
                        </grid-layout-item>
                    </grid-layout>
                </div>
            </div>

            <div
                class="pb-16 bg-center bg-no-repeat md:pb-32"
                :style="{
                    'background-image': 'url(/img/misc-background.svg)',
                    'background-position': 'top 0 left -50px',
                    'background-size': '462px 600px'
                }"
            >
                <div class="container max-w-3xl mx-auto">
                    <grid-layout class="flex-col-reverse items-center lg:flex-row-reverse">
                        <grid-layout-item class="mb-12 lg:mb-0 lg:w-1/2">
                            <h3 class="mb-4 text-2xl font-semibold leading-tight tracking-tight xs:text-3xl font-noto-sans text-grey-darkest">
                                Choose the right tenants
                            </h3>

                            <p class="mb-12 text-lg lg:text-xl">
                                Only spend your time with quality prospective tenants, and reduce the time your properties are left vacant.
                            </p>

                            <div class="flex flex-col items-start mb-12 md:flex-row">
                                <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                    <base-svg
                                        name="icon-inbox"
                                        class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                                    />
                                </div>

                                <div class="flex-grow md:pl-6">
                                    <div class="mb-1 text-sm uppercase text-grey-dark">
                                        Applications
                                    </div>

                                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Applications, built in
                                    </h6>

                                    <p class="mb-3">
                                        Every Rentora listing has a built-in rental application. Tenants submit applications
                                        directly to you, with all the information you need to make an informed decision.
                                    </p>

                                    <p>
                                        See an applicant's employment status, income, employment history and past landlords contact details.
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col items-start md:flex-row">
                                <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                    <base-svg
                                        name="icon-chat"
                                        class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                                    />
                                </div>

                                <div class="flex-grow md:pl-6">
                                    <div class="mb-1 text-sm uppercase text-grey-dark">
                                        Messaging
                                    </div>

                                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Vet with anonymity
                                    </h6>

                                    <p>
                                        Need more information from a prospective tenant? Chat to applicants via our messaging
                                        service. There's no need to share your contact details until you're ready.
                                    </p>
                                </div>
                            </div>
                        </grid-layout-item>
                        <grid-layout-item class="mb-6 lg:mb-0 lg:w-1/2">
                            <img
                                src="/img/applicants.svg"
                                class="w-full mx-auto"
                                :style="{
                                    'max-width': '439px'
                                }"
                            >
                        </grid-layout-item>
                    </grid-layout>
                </div>
            </div>
        </div>

        <div class="container max-w-3xl mx-auto">
            <div class="pt-24 pb-12">
                <h3 class="mb-4 mb-10 text-2xl font-semibold leading-tight tracking-tight md:text-center xs:text-3xl font-noto-sans text-grey-darkest">
                    Don't just take our word for it
                </h3>

                <blockquote class="relative max-w-lg mx-auto">
                    <base-svg
                        name="misc-quote"
                        class="absolute w-16 h-16 -mt-6 -ml-6 fill-primary-blue-lightest pin-t pin-l"
                    />

                    <div class="relative text-lg">
                        <p class="mb-3">
                            I created a listing on Rentora and shared it on a local Facebook group. Within 24 hours I had
                            about 15 applications! I sifted through them and messaged 3 of them to arrange a viewing and a face-to-face chat.
                        </p>

                        <p class="mb-3">
                            Not 7 days later and the tenant I’d chosen was in and had paid the first month's rent through
                            Rentora’s rent collection service. Brilliant!
                        </p>

                        <cite>
                            John W, Private Landlord
                        </cite>
                    </div>
                </blockquote>
            </div>
        </div>

        <div class="flex flex-col items-center max-w-xl p-3 mx-auto mb-12 rounded lg:flex-row bg-blue-lightest xs:p-6 lg:p-12">
            <div class="mb-6 lg:mb-0 lg:w-1/2">
                <h6 class="text-2xl font-semibold md:text-3xl font-noto-sans text-blue-darkest">
                    Ready to get started?
                </h6>

                <p class="text-lg lg:text-xl">
                    Create an account in a couple of minutes
                </p>
            </div>

            <div class="flex flex-col justify-center w-full lg:w-1/2 md:flex-row lg:justify-end">
                <laroute-link
                    :to="{
                        name: 'auth.users.create',
                        params: {
                            intended: $laroute.route('dashboard.landlord.listings.create', {
                                welcome: '✓'
                            })
                        }
                    }"
                    class="w-full mb-3 shadow-md md:mb-0 md:mr-3 md:w-auto btn btn-blue-darkest btn-xl"
                >
                    Get started
                </laroute-link>

                <callback-requests-create-button
                    class="w-full shadow-md md:w-auto btn btn-white btn-xl"
                >
                    Request a callback
                </callback-requests-create-button>
            </div>
        </div>

        <footer class="py-6 pb-24 text-white bg-blue-bg-darker md:py-12">
            <div class="container max-w-3xl mx-auto">
                <div class="flex flex-col items-center justify-center pb-6 text-xs text-blue-lighter">
                    <div class="flex-none px-3">
                        <router-link :to="{ name: 'www.policies.terms.index' }">
                            Terms,
                        </router-link>

                        <router-link :to="{ name: 'www.policies.privacy.index' }">
                            Privacy
                        </router-link>

                        &amp;

                        <router-link :to="{ name: 'www.policies.cookies.index' }">
                            Cookie
                        </router-link>

                        policies
                    </div>

                    <div class="flex-none px-3">
                        &copy; {{ $moment().format('Y') }} Tenant Manager Ltd.
                    </div>
                </div>
            </div>
        </footer>
    </div>
</template>

<script>
    import _ from 'lodash'
    import HeadMixin from '../../../mixins/head'

    export default {
        mixins: [
            HeadMixin
        ],
        methods: {
            head () {
                return {
                    title: [
                        'Market your properties for free',
                        'Listings for private landlords and estate agents'
                    ],
                    description: 'Publicly list your properties, and/or invite tenants to apply with a unique link. Every Rentora listing has a built-in rental application.',
                    meta: [
                        {
                            property: 'og:image',
                            content: _.url('/img/head/og-property-listings.png')
                        },
                        {
                            name: 'twitter:image',
                            content: _.url('/img/head/twitter-property-listings.png')
                        }
                    ]
                }
            }
        }
    }
</script>
